import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Building2, MapPin, Calendar, IndianRupee, RefreshCw } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const INR = (n) => '\u20B9' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

export default function Vendors() {
  const [rollup, setRollup] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadAll = async () => {
    setLoading(true);
    try {
      const [rl, vl] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/vendors/rollup`),
        axios.get(`${BACKEND_URL}/api/accounting/vendors`).catch(() => ({ data: [] })),
      ]);
      setRollup((rl.data && rl.data.items) || []);
      setVendors(Array.isArray(vl.data) ? vl.data : []);
    } catch (e) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const filtered = rollup.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.vendorName || '').toLowerCase().includes(q)
      || (r.supplierRef || '').toLowerCase().includes(q)
      || (r.category || '').toLowerCase().includes(q);
  });

  const totalSpend = filtered.reduce((s, r) => s + (r.totalFinal || 0), 0);
  const totalLines = filtered.reduce((s, r) => s + (r.lineCount || 0), 0);

  return (
    <div className="space-y-6" data-testid="vendors-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-3xl lg:text-4xl font-heading font-bold text-slate-900 tracking-tight">Vendors</h1>
          <p className="text-sm text-slate-500 mt-1">Procurement rollup across every cost line on every trip.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search supplier or category…"
            className="h-10 w-64 rounded-lg"
            data-testid="vendors-search"
          />
          <Button onClick={loadAll} variant="outline" size="sm" className="h-10 rounded-lg" disabled={loading} data-testid="vendors-refresh">
            <RefreshCw className={'h-3.5 w-3.5 mr-1.5 ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-5" data-testid="vendor-stat-suppliers">
          <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Suppliers</p>
          <p className="text-3xl font-heading font-bold text-slate-900 mt-1.5">{filtered.length}</p>
        </Card>
        <Card className="p-5" data-testid="vendor-stat-spend">
          <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Total spend</p>
          <p className="text-3xl font-heading font-bold text-emerald-700 mt-1.5">{INR(totalSpend)}</p>
        </Card>
        <Card className="p-5" data-testid="vendor-stat-lines">
          <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Cost lines</p>
          <p className="text-3xl font-heading font-bold text-slate-900 mt-1.5">{totalLines}</p>
          <p className="text-[11px] text-slate-400 mt-0.5">{vendors.length} canonical vendor{vendors.length !== 1 && 's'} registered</p>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Supplier rollup</h3>
          <Link to="/accounting" className="text-xs text-teal-700 hover:underline">Manage canonical vendors →</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="vendor-rollup-table">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-2 font-semibold">Vendor Name</th>
                <th className="text-left px-3 py-2 font-semibold">Invoice / Ref No</th>
                <th className="text-left px-3 py-2 font-semibold w-32">Category</th>
                <th className="text-right px-3 py-2 font-semibold w-24">Lines</th>
                <th className="text-right px-3 py-2 font-semibold w-32">Cost</th>
                <th className="text-right px-3 py-2 font-semibold w-32">Margin</th>
                <th className="text-right px-3 py-2 font-semibold w-36">Total spend</th>
                <th className="text-left px-3 py-2 font-semibold w-32">Last booking</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center text-slate-400 py-8">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center text-slate-400 italic py-10">
                  No supplier references found yet. Add a "Supplier ref" on any trip cost item to populate this view.
                </td></tr>
              )}
              {!loading && filtered.map((r, i) => (
                <tr key={(r.vendorName || '') + '-' + (r.supplierRef || '') + '-' + i} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={'vendor-row-' + i}>
                  <td className="px-5 py-3 font-medium text-slate-900">
                    <div className="flex items-start gap-2">
                      <Building2 className="h-4 w-4 text-slate-400 mt-0.5" />
                      <div>
                        <div className="truncate max-w-md font-semibold">{r.vendorName || r.supplierRef || '—'}</div>
                        {r.destinations && r.destinations.length > 0 && (
                          <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3" />
                            {r.destinations.slice(0, 3).join(', ')}{r.destinations.length > 3 ? '…' : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-slate-600 font-mono text-xs">{r.supplierRef || '—'}</td>
                  <td className="px-3 py-3 text-slate-600 capitalize">{r.category || '—'}</td>
                  <td className="px-3 py-3 text-right text-slate-700">{r.lineCount}</td>
                  <td className="px-3 py-3 text-right text-slate-700 font-mono text-xs">{INR(r.totalCostInr)}</td>
                  <td className="px-3 py-3 text-right text-emerald-700 font-mono text-xs">{INR(r.totalMargin)}</td>
                  <td className="px-3 py-3 text-right font-bold text-teal-700">{INR(r.totalFinal)}</td>
                  <td className="px-3 py-3 text-slate-500 text-xs">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {r.lastBooking ? new Date(r.lastBooking).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
